import Phaser from "phaser";
import type { GameApp } from "../app/GameApp";
import { mountOverlay, showToast, type DomOverlay } from "@/ui/domHelpers";
import { TEMPLE_RESET_FEAR_COST, TEMPLE_BUFF_COST, TEMPLE_BUFF_DURATION_TURNS, type Stat } from "../core/roscha";

const STATS: Array<{ stat: Stat; label: string }> = [
  { stat: "strength", label: "Сила" },
  { stat: "dexterity", label: "Ловкость" },
  { stat: "intelligence", label: "Интеллект" },
  { stat: "wisdom", label: "Мудрость" },
  { stat: "constitution", label: "Телосложение" },
];

interface StatBtnRefs {
  stat: Stat;
  btn: HTMLButtonElement;
  valEl: HTMLElement;
  nextEl: HTMLElement;
}

export class TempleOverlay extends Phaser.Scene {
  private app!: GameApp;
  private overlay?: DomOverlay;
  private goldEl?: HTMLElement;
  private fearEl?: HTMLElement;
  private fearDescEl?: HTMLElement;
  private resetBtn?: HTMLButtonElement;
  private hpBtn?: HTMLButtonElement;
  private energyBtn?: HTMLButtonElement;
  private hpCostEl?: HTMLElement;
  private energyCostEl?: HTMLElement;
  private hpStateEl?: HTMLElement;
  private energyStateEl?: HTMLElement;
  private statBtns: StatBtnRefs[] = [];
  private unsubWorld?: () => void;
  private unsubEnergy?: () => void;
  private onWake = () => this.refresh();
  private readonly MAX_ENERGY = 100;

  constructor() {
    super("TempleOverlay");
  }

  create() {
    this.app = this.game.registry.get("app") as GameApp;
    this.mount();
    this.refresh();
    this.unsubWorld = this.app.onWorldState(() => this.refresh());
    this.unsubEnergy = this.app.onEnergyUpdate(() => this.refresh());
    this.events.on(Phaser.Scenes.Events.WAKE, this.onWake);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => this.cleanup());
  }

  private mount() {
    const statsHtml = STATS.map((s) => `
      <button class="dom-btn temple__stat-btn" data-stat="${s.stat}">
        <span class="temple__stat-label">${s.label}</span>
        <span class="temple__stat-progress"><strong data-stat-val="${s.stat}">0</strong> → <em data-stat-next="${s.stat}">1</em></span>
      </button>
    `).join("");

    const bodyHtml = `
      <div class="dom-resource-row">
        <span><span class="icon-gold"></span> <strong data-bind="gold">0</strong></span>
        <span>Накопленный страх <strong data-bind="fear">0/3</strong></span>
      </div>

      <section class="temple__section">
        <h3 class="dom-section__title">Восстановление</h3>
        <div class="temple__action-row">
          <button class="dom-btn dom-btn--success" data-action="restore-hp">
            <span>Восстановить HP <strong data-bind="hp-state">— / —</strong></span>
            <span class="temple__cost"><strong data-bind="hp-cost">0</strong> <span class="icon-gold icon-gold--sm"></span></span>
          </button>
          <button class="dom-btn dom-btn--success" data-action="restore-energy">
            <span>Восстановить энергию <strong data-bind="energy-state">— / —</strong></span>
            <span class="temple__cost"><strong data-bind="energy-cost">0</strong> <span class="icon-gold icon-gold--sm"></span></span>
          </button>
        </div>
      </section>

      <section class="temple__section">
        <h3 class="dom-section__title">Очищение</h3>
        <p class="dom-section__desc" data-bind="fear-desc">Сбросить накопленный страх до нуля.</p>
        <div class="temple__action-row">
          <button class="dom-btn dom-btn--success" data-action="reset">
            Сбросить страх <span class="temple__cost">${TEMPLE_RESET_FEAR_COST} <span class="icon-gold icon-gold--sm"></span></span>
          </button>
        </div>
      </section>

      <section class="temple__section">
        <h3 class="dom-section__title">Благословение</h3>
        <p class="dom-section__desc">
          Временное +1 к выбранной характеристике на ${TEMPLE_BUFF_DURATION_TURNS} ходов. Стоимость: ${TEMPLE_BUFF_COST} <span class="icon-gold icon-gold--sm"></span>.
        </p>
        <div class="temple__stats">${statsHtml}</div>
        <div class="temple__active-buffs" data-bind="active-buffs"></div>
      </section>
    `;

    this.overlay = mountOverlay(this, {
      title: "Храм",
      subtitle: "Очищение и благословения",
      bodyHtml,
      onClose: () => this.app.openTown(),
      inBody: true,
    });

    const root = this.overlay.root;
    this.goldEl = root.querySelector<HTMLElement>('[data-bind="gold"]') ?? undefined;
    this.fearEl = root.querySelector<HTMLElement>('[data-bind="fear"]') ?? undefined;
    this.fearDescEl = root.querySelector<HTMLElement>('[data-bind="fear-desc"]') ?? undefined;
    this.resetBtn = root.querySelector<HTMLButtonElement>('[data-action="reset"]') ?? undefined;
    this.hpBtn = root.querySelector<HTMLButtonElement>('[data-action="restore-hp"]') ?? undefined;
    this.energyBtn = root.querySelector<HTMLButtonElement>('[data-action="restore-energy"]') ?? undefined;
    this.hpCostEl = root.querySelector<HTMLElement>('[data-bind="hp-cost"]') ?? undefined;
    this.energyCostEl = root.querySelector<HTMLElement>('[data-bind="energy-cost"]') ?? undefined;
    this.hpStateEl = root.querySelector<HTMLElement>('[data-bind="hp-state"]') ?? undefined;
    this.energyStateEl = root.querySelector<HTMLElement>('[data-bind="energy-state"]') ?? undefined;
    this.statBtns = STATS.map((s) => ({
      stat: s.stat,
      btn: root.querySelector<HTMLButtonElement>(`[data-stat="${s.stat}"]`)!,
      valEl: root.querySelector<HTMLElement>(`[data-stat-val="${s.stat}"]`)!,
      nextEl: root.querySelector<HTMLElement>(`[data-stat-next="${s.stat}"]`)!,
    }));

    this.resetBtn?.addEventListener("click", () => {
      if (this.resetBtn?.disabled) return;
      this.app.resetFear(TEMPLE_RESET_FEAR_COST);
    });
    this.hpBtn?.addEventListener("click", () => {
      if (this.hpBtn?.disabled) return;
      void this.tryRestore("hp");
    });
    this.energyBtn?.addEventListener("click", () => {
      if (this.energyBtn?.disabled) return;
      void this.tryRestore("energy");
    });
    this.statBtns.forEach(({ stat, btn }) => {
      btn.addEventListener("click", () => {
        if (btn.disabled) return;
        this.app.applyTempleBuff(stat, TEMPLE_BUFF_COST);
      });
    });
  }

  private async tryRestore(kind: "hp" | "energy") {
    const res = await this.app.restoreTemple(kind);
    if (!res) return;
    if (!res.ok && res.reason) showToast(res.reason, "error");
  }

  private refresh() {
    const hero = this.app.getWorldState()?.hero;
    if (!hero || !this.overlay) return;

    const gold = hero.inventory.resources.gold ?? 0;
    const fear = hero.fearAccumulated;
    const canReset = gold >= TEMPLE_RESET_FEAR_COST && fear > 0;
    const canBuff = gold >= TEMPLE_BUFF_COST;

    const hp = hero.hp;
    const maxHp = hero.maxHp;
    const hpCost = hp < maxHp ? Math.max(1, Math.ceil((maxHp - hp) / maxHp * 30)) : 0;
    const energy = this.app.getEnergy();
    const energyCost = energy < this.MAX_ENERGY ? Math.max(1, Math.ceil((this.MAX_ENERGY - energy) / this.MAX_ENERGY * 30)) : 0;
    const canRestoreHp = hp < maxHp && gold >= hpCost;
    const canRestoreEnergy = energy < this.MAX_ENERGY && gold >= energyCost;

    if (this.hpStateEl) this.hpStateEl.textContent = `${hp} / ${maxHp}`;
    if (this.energyStateEl) this.energyStateEl.textContent = `${energy} / ${this.MAX_ENERGY}`;
    if (this.hpCostEl) this.hpCostEl.textContent = String(hpCost);
    if (this.energyCostEl) this.energyCostEl.textContent = String(energyCost);
    if (this.hpBtn) {
      this.hpBtn.disabled = !canRestoreHp;
      this.hpBtn.setAttribute("data-disabled", String(!canRestoreHp));
    }
    if (this.energyBtn) {
      this.energyBtn.disabled = !canRestoreEnergy;
      this.energyBtn.setAttribute("data-disabled", String(!canRestoreEnergy));
    }

    if (this.goldEl) this.goldEl.textContent = String(gold);
    if (this.fearEl) this.fearEl.textContent = `${fear}/3`;
    if (this.fearDescEl) this.fearDescEl.textContent = `Сбросить накопленный страх до нуля. Сейчас: ${fear}.`;
    if (this.resetBtn) {
      this.resetBtn.disabled = !canReset;
      this.resetBtn.setAttribute("data-disabled", String(!canReset));
    }
    for (const { stat, btn, valEl, nextEl } of this.statBtns) {
      const val = hero.baseStats[stat] ?? 0;
      valEl.textContent = String(val);
      nextEl.textContent = String(val + 1);
      btn.disabled = !canBuff;
      btn.setAttribute("data-disabled", String(!canBuff));
    }
    const buffsEl = this.overlay.root.querySelector<HTMLElement>('[data-bind="active-buffs"]');
    if (buffsEl) {
      const buffs = hero.activeBuffs ?? [];
      const turn = this.app.getWorldState()?.turn ?? 0;
      const labelOf: Record<Stat, string> = { strength: "Сила", dexterity: "Ловкость", intelligence: "Интеллект", wisdom: "Мудрость", constitution: "Телосложение" };
      buffsEl.innerHTML = buffs.length === 0
        ? ""
        : `<p class="temple__buffs-title">Активные баффы:</p><ul class="temple__buffs-list">${buffs.map((b) => `<li>+${b.amount} ${labelOf[b.stat]} (осталось ${Math.max(0, b.expiresAtTurn - turn)} ходов)</li>`).join("")}</ul>`;
    }
  }

  private cleanup() {
    this.events.off(Phaser.Scenes.Events.WAKE, this.onWake);
    this.unsubWorld?.();
    this.unsubWorld = undefined;
    this.unsubEnergy?.();
    this.unsubEnergy = undefined;
    this.overlay = undefined;
    this.statBtns = [];
    this.goldEl = undefined;
    this.fearEl = undefined;
    this.fearDescEl = undefined;
    this.resetBtn = undefined;
    this.hpBtn = undefined;
    this.energyBtn = undefined;
    this.hpCostEl = undefined;
    this.energyCostEl = undefined;
    this.hpStateEl = undefined;
    this.energyStateEl = undefined;
  }
}
